import React from 'react';
import { Breakpoints } from '@cvent/carina/components/Breakpoints/Breakpoints';
import { TimezoneProvider } from '@context/TimezoneContext';
import { AttendeeMessagingProvider } from '@context/AttendeeMessagingContext';
import { EnrolledSessionsProvider } from '@context/EnrolledSessionsContext';
import { WebcastStreamStatusProvider } from '@context/WebcastStreamStatusContext';
import { StreamClientProvider } from '@context/StreamClientContext';
import { useRenderMode } from '@hooks/useRenderMode';
import { NotificationsProvider } from '@context/NotificationsContext';
import { GlobalStyles } from '@components/app/GlobalStyles';
import ThemeProvider from '@context/ThemeProvider';
import { FlagsProvider } from '@cvent/carina/components/FlagsProvider';
import ThemeSwitcher from './ThemeSwitcher';
import { AppReducer } from './AppReducer';

const carinaFlags = { zIndexUpdates: true };
export interface AppProvidersProps {
  children: React.ReactNode;
  locale: string;
  isAuthenticated?: boolean;
}

function AuthenticatedProviders({
  children,
  isAuthenticated
}: Pick<AppProvidersProps, 'children' | 'isAuthenticated'>): JSX.Element {
  return isAuthenticated ? (
    <NotificationsProvider>
      <EnrolledSessionsProvider>
        <StreamClientProvider>
          <AttendeeMessagingProvider>
            <WebcastStreamStatusProvider>{children}</WebcastStreamStatusProvider>
          </AttendeeMessagingProvider>
        </StreamClientProvider>
      </EnrolledSessionsProvider>
    </NotificationsProvider>
  ) : (
    <>{children}</>
  );
}

export function AppProviders({ children, locale, isAuthenticated }: AppProvidersProps): JSX.Element {
  // Used to force Breakpoints to update when the client is hydrated
  const renderMode = useRenderMode();

  // Remove breakpoints override when https://jira.cvent.com/browse/CARINA-4049
  // is done and carina - layout is updated
  return (
    <AppReducer locale={locale}>
      <ThemeSwitcher />
      <Breakpoints ssrSize="m" s={336} m={560} l={1080} xl={1464} key={renderMode}>
        <ThemeProvider>
          <GlobalStyles />
          <TimezoneProvider>
            <FlagsProvider flags={carinaFlags}>
              <AuthenticatedProviders isAuthenticated={isAuthenticated}>{children}</AuthenticatedProviders>
            </FlagsProvider>
          </TimezoneProvider>
        </ThemeProvider>
      </Breakpoints>
    </AppReducer>
  );
}
